<template>
    <div class="splicing-table">
        <div class="splicing-table-inner-container d-flex justify-space-between mb-4">
            <h2 class="splicing-table-title">Splicing</h2>
            <div>
                <v-btn 
                    color="accent" 
                    :loading="btnLoading"
                    :style="{'text-transform': 'none'}"
                    @click="openClosureToExport"
                >
                    <v-icon>mdi-download</v-icon>
                    Export
                </v-btn>
            </div>
        </div>
        <div class="splicing-titles-container">
            <div class="splicing-titles-tray">
                <p class="title-tray-item">Tray</p>
            </div>
            <div class="splicing-titles-origin">
                <p class="title-tray-origin">Origin</p>
            </div>
            <div class="splicing-titles-destination">
                <p class="title-tray-origin">Destination</p>
            </div>
        </div>
        <v-data-table
            :headers="headers"
            :items="allConnections"
            :items-per-page.sync="rowsPerPage"
            :items-per-page-text="rowsPerPageText"
            :items-per-page-items="rowsPerPageItems"
            :style="{height: '100%'}"
            :loading="loading"
            class="splicing-data-table elevation-1"
            :footer-props="{
                'items-per-page-options': [15, 50, 100]
            }"
        >
        <template v-slot:item="props">
            <tr>
                <td>{{props.item.trayMaterial}}</td>
                <td>{{props.item.trayLabel}}</td>
                <td>{{props.item.trayFace}}</td>
                <td>{{props.item.trayNumber}}</td>
                <td>{{props.item.trayPosition}}</td>
                <td class="type-row">{{props.item.originType}}</td>
                <td>{{getMaterialName(props.item.originMaterial, props.item.originConnectionType)}}</td>
                <td>{{props.item.originLabel}}</td>
                <td v-if="props.item.originConnectionType === 'fiber'">
                    <div class="d-flex align-center">
                        <span v-if="props.item.originTubeColor" class="cube cube-right" :style="{'background-color': getTubeColor(props.item.originTubeColor)}" />
                        <span v-if="props.item.originFiberColor" class="circle circle-right" :style="{'background-color': getFiberColor(props.item.originFiberColor)}" />
                        <div :style="{minWidth:'58px'}">
                            {{props.item.originIdentificators}}
                        </div>
                    </div>
                </td>
                <td v-if="props.item.originConnectionType === 'port'">
                    <div class="d-flex align-center">
                        <span class="triangle triangle-right" />
                        {{props.item.originIdentificators}}
                    </div>
                </td>
                <td class="type-row">{{props.item.mode}}</td>
                <td class="type-row" v-if="props.item.destinationConnectionType === 'fiber'">
                    <div class="d-flex align-center">
                        <div :style="{minWidth:'58px'}">
                            {{props.item.destinationIdentificators}}
                        </div>
                        <span v-if="props.item.destinationFiberColor" class="circle circle-left" :style="{'background-color': getFiberColor(props.item.destinationFiberColor)}" />
                        <span v-if="props.item.destinationTubeColor" class="cube cube-left" :style="{'background-color': getTubeColor(props.item.destinationTubeColor)}" />
                    </div>
                </td>
                <td class="type-row" v-if="props.item.destinationConnectionType === 'port'">
                    <div class="d-flex align-center">
                        {{props.item.destinationIdentificators}}
                        <span class="triangle triangle-left" />
                    </div>
                </td>
                <td>{{props.item.destinationLabel}}</td>
                <td>{{getMaterialName(props.item.destinationMaterial, props.item.destinationConnectionType)}}</td>
                <td>{{props.item.destinationType}}</td>
            </tr>
        </template>
    
        <template v-slot:pageText="props">
            {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
            {{ props.itemsLength }}
        </template>
        </v-data-table>
    </div>
    
  </template>
  
  <script>
  import { mapActions } from "vuex";
  export default {
    name: "SplicingTable",
    props: {
      
    },
    data() {
      return {
        fibersPortsCache: {
            fiber: {},
            port: {}
        },
        allConnections: [],
        fiberColors: [],
        loading: false,
        btnLoading: false,
        splitterMaterials: [],
        cableMaterials: [],
        rowsPerPageText: this.$i18n.t("tables.RowsPerPage"),
        rowsPerPageItems: [15, 50, 100],
        rowsPerPage: 15
      };
    },
    computed: {
      headers() {
        return [
          {
            text: "Material",
            align: "left",
            value: "trayMaterial",
            width: "7.14%"
          },
          {
            text: "Label",
            align: "left",
            value: "trayLabel",
            width: "7.14%"
          },
          {
            text: "Face",
            align: "left",
            value: "trayFace",
            width: "5.14%"
          },
          {
            text: "Number",
            align: "left",
            value: "trayNumber",
            width: "3.14"
          },
          {
            text: "Position",
            align: "left",
            value: "trayPosition",
            width: "3.14%"
          },
          {
            text: "Type",
            align: "left",
            value: "originType",
            width: "5.14",
            class: "type-row"
          },
          {
            text: "Material",
            align: "left",
            value: "originMaterial",
            width: "7.14",
          },
          {
            text: "Label",
            align: "left",
            value: "originLabel",
            width: "7.14"
          },
          {
            text: "TUBE/FIBER | PORT",
            align: "left",
            value: "originIdentificators",
            width: "14.89"
          },
          {
            text: "Mode",
            align: "center",
            value: "mode",
            width: "5.64",
            class: "type-row"
          },
          {
            text: "TUBE/FIBER | PORT",
            align: "left",
            value: "destinationIdentificators",
            width: "14.89",
            class: "type-row"
          },
          {
            text: "Label",
            align: "left",
            value: "destinationLabel",
            width: "7.14"
          },
          {
            text: "Material",
            align: "left",
            value: "destinationMaterial",
            width: "7.14"
          },
          {
            text: "Type",
            align: "left",
            value: "destinationType",
            width: "5.14",
          },
        ];
      }
    },
    mounted(){
        this.getLists();
        this.getTableInfo();
    },
    methods: {
        ...mapActions(["fetchCablesInAndOut", "fetchStockMaterialsList", "fetchObjectSplicingConnections", "GET_ALL_LISTS", "downloadSplicePlan"]),
        async getTableInfo(){
            const finalConnections = [];
            const closureUuid = localStorage.getItem('closureUuid');
            const isTechLocal = localStorage.getItem('techLocal');
            const objEndpoint = isTechLocal === 'true' ? 'technicallocals' : 'closures';
            this.loading = true;
            this.btnLoading = true;
            const connections = await this.getAllConnections(objEndpoint, closureUuid);
            const object = (await this.getObjectCables(closureUuid, objEndpoint));
            //populate object with all fiber and ports
            object.cablein.forEach((cable)=>{
                cable.fibers.forEach((fiber)=>{
                    this.fibersPortsCache['fiber'][fiber.fb_id] = {
                        fiber,
                        cable
                    };
                })
            });
            object.cableout.forEach((cable)=>{
                cable.fibers.forEach((fiber)=>{
                    this.fibersPortsCache['fiber'][fiber.fb_id] = {
                        fiber,
                        cable
                    };
                })
            });
            object.splitters.forEach((splitter)=>{
                splitter.inPorts.forEach((port)=>{
                    this.fibersPortsCache['port'][port.pt_id] = {
                        port,
                        splitter
                    };
                })
            });
            object.splitters.forEach((splitter)=>{
                splitter.outPorts.forEach((port)=>{
                    this.fibersPortsCache['port'][port.pt_id] = {
                        port,
                        splitter
                    };
                })
            });
            //create array to be sent to table
            connections.forEach(connection => {
                let tray;
                let originObject = connection.origin.type ? this.fibersPortsCache[connection.origin.type][connection.origin.id] : null;
                let destinationObject = connection.destination.type ? this.fibersPortsCache[connection.destination.type][connection.destination.id] : null;
                const origin = this.getOriginObject(originObject,connection.origin.type, connection.origin);
                const destination = this.getDestinationObject(destinationObject,connection.destination.type, connection.destination);
                if(connection.hasOwnProperty('tray')){
                    tray = {
                        trayMaterial: connection.tray.material_label ? connection.tray.material_label : '-',
                        trayLabel: connection.tray.label ? connection.tray.label : connection.tray.id,
                        trayFace: connection.tray.face ? connection.tray.face : '-',
                        trayNumber: connection.tray.index ? connection.tray.index : '-',
                        trayPosition: connection.position.index ? connection.position.index: '-'
                    };
                }else{
                    tray = {
                        trayMaterial: "-",
                        trayLabel: "-",
                        trayFace: "-",
                        trayNumber: "-",
                        trayPosition: '-'
                    };
                }
                const finalObj = {...tray, ...origin, ...destination, mode: connection.mode};
                finalConnections.push(finalObj);
            });
            this.allConnections = [...finalConnections];
            this.loading = false;
            this.btnLoading = false;
        },
        getOriginObject(object, type, originObject){
            let obj;
            if(object){
                if(type === "fiber"){
                    obj = {
                        originType: originObject.logical_type ? originObject.logical_type.client_label ? originObject.logical_type.client_label : originObject.logical_type.code : '-',
                        originConnectionType: type,
                        originMaterial: object && object.hasOwnProperty('cable') ? object.cable.cb_material_id : null,
                        originLabel: object && object.hasOwnProperty('fiber') ? object.cable.cb_label ? object.cable.cb_label : `CB:${object.fiber.fb_cable_id}` : '-',
                        originTubeColor: object && object.hasOwnProperty('fiber') ? object.fiber.fb_nr_tube : null,
                        originFiberColor: object && object.hasOwnProperty('fiber') ? object.fiber.fb_color : null,
                        originIdentificators: object && object.hasOwnProperty('fiber') ? `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube} (${object.fiber.fb_nr_in_cable})` : '-',
                    }
                }else{
                    obj = {
                        originType: '-',
                        originConnectionType: type,
                        originMaterial: object.splitter.sp_material_id,
                        originLabel: object.splitter.sp_label ? object.splitter.sp_label : `SP${object.splitter.sp_id}`,
                        originIdentificators: `P${object.port.pt_nr_in_splitter}`
                    }
                }
            } else {
                obj = {
                    originType: '-',
                    originConnectionType: "fiber",
                    originMaterial: '-',
                    originLabel: '-',
                    originTubeColor: null,
                    originFiberColor: null,
                    originIdentificators: '-'
                }
            }
            return obj;
        },
        getDestinationObject(object, type, destinationObject){
            let obj;
            if(object){
                if(type === "fiber"){
                    obj = {
                        destinationType: destinationObject.logical_type ? destinationObject.logical_type.client_label ? destinationObject.logical_type.client_label : destinationObject.logical_type.code : '-',
                        destinationConnectionType: type,
                        destinationMaterial: object && object.hasOwnProperty('cable') ? object.cable.cb_material_id : null,
                        destinationLabel: object && object.hasOwnProperty('fiber') ? object.cable.cb_label ? object.cable.cb_label : `CB:${object.fiber.fb_cable_id}` : '-',
                        destinationTubeColor: object && object.hasOwnProperty('fiber') ? object.fiber.fb_nr_tube : null,
                        destinationFiberColor: object && object.hasOwnProperty('fiber') ? object.fiber.fb_color : null,
                        destinationIdentificators: object && object.hasOwnProperty('fiber') ? `T${object.fiber.fb_nr_tube}/F${object.fiber.fb_nr_in_tube} (${object.fiber.fb_nr_in_cable})` : '-',
                    }
                }else{
                    obj = {
                        destinationType: '-',
                        destinationConnectionType: type,
                        destinationMaterial: object.splitter.sp_material_id,
                        destinationLabel: object.splitter.sp_label ? object.splitter.sp_label : `SP${object.splitter.sp_id}`,
                        destinationIdentificators: `P${object.port.pt_nr_in_splitter}`
                    }
                }
            }else{
                obj = {
                    destinationType: '-',
                    destinationConnectionType: "fiber",
                    destinationMaterial: '-',
                    destinationLabel: '-',
                    destinationTubeColor: null,
                    destinationFiberColor:null,
                    destinationIdentificators: '-'
                }
            }
            return obj;
        },
        async getObjectCables(objectUuid, endpoint) {
            try {
                let obj = await this.fetchCablesInAndOut({
                    endpoint: endpoint,
                    objectUUID: objectUuid
                });
                return obj[0];
            } catch (e) {
                console.error(e);
            }
        },
        async getAllConnections(objectEndpoint, objectUuid) {
            try {
                const response = await this.fetchObjectSplicingConnections({
                    objectEndpoint: objectEndpoint,
                    objectUUID: objectUuid
                });
                return response;
            } catch (e) {
                console.error(e);
            }
        },
        async getLists(){
            try{
                const lists = await this.GET_ALL_LISTS();
                this.fiberColors = [...lists.data.data.list_fo_color];
                this.splitterMaterials = await this.fetchStockMaterialsList(
                    {
                        material_family_id: "sp"
                    }
                );
                this.cableMaterials = await this.fetchStockMaterialsList(
                    {
                        append: "family_specific_fields",
                        material_family_id: "ca",
                    }
                );
            }catch(err){
                console.log(err);
            }
        },
        getFiberColor(colorId){
            let finalColor;
            this.fiberColors.forEach(color => {
                if(color.code === String(colorId)){
                    finalColor = color.libelle;
                }
            })
            return finalColor;
        },
        getTubeColor(nr) {
            let colorNr = nr % 12;
            if (colorNr === 0) {
                colorNr = 12;
            }
            switch (colorNr) {
                case 1:
                    return "#0070C0";
                case 2:
                    return "#FD8408";
                case 3:
                    return "#00B050";
                case 4:
                    return "#974706";
                case 5:
                    return "#A6A6A6";
                case 6:
                    return "#FFFFFF";
                case 7:
                    return "#FF0000";
                case 8:
                    return "#000000";
                case 9:
                    return "#FFFF00";
                case 10:
                    return "#CE1C93";
                case 11:
                    return "#FBA5A3";
                case 12:
                    return "#7AD3F2";
            }
        },
        getMaterialName(id, type){
            let label = '-';
            if(id){
                if(type === 'fiber'){
                    this.cableMaterials.forEach(cable =>{
                        if(cable.sm_id === id){
                            label = cable.sm_short_description ? `${cable.sm_short_description}` : `${cable.sm_description}`
                        }
                    });
                }else{
                    this.splitterMaterials.forEach(splitter =>{
                        if(splitter.sm_id === id){
                            label = splitter.sm_short_description ? `${splitter.sm_short_description}` : `${splitter.sm_description}`
                        }
                    });
                }
            }
            return label;
        },
        async openClosureToExport() {
            this.btnLoading = true;
            const closureUuid = localStorage.getItem('closureUuid');
            const workspaceUuid = sessionStorage.getItem('workspace_uuid');
            const isTechLocal = localStorage.getItem('techLocal');
            const objectType = isTechLocal === 'true' ? 'technical_local' : 'closure';
            const object = objectType === 'closure' ? 
                [
                    {
                        uuid: closureUuid,
                        object_type: objectType
                    }
                ] : {
                        uuid: closureUuid,
                        object_type: objectType
                    }
                        
            try {
                let data = {
                    type: "excel",
                    version: objectType === 'closure' ? "splice_plan_v1" : "tech_local_splice_plan",
                    objects: {
                        workspace_uuid: workspaceUuid,
                        object
                    }
                }
                const exports = await this.downloadSplicePlan(data);
                if (exports.file_url_public) {
                    window.open(exports.file_url_public)
                } else {
                    this.$toasted.global.toast_error(this.$i18n.t("Fail"));
                }
            } catch (e) {
                console.log(e);
            } finally {
                this.btnLoading = false;
            }
        },
    }
  };
  </script>
<style>
.splicing-table{
    padding: 20px 40px 20px 40px;
    background-color: #fafbfc;
    display: flex;
    flex-direction: column;
    position: relative;
}
.splicing-titles-container{
    display: flex;
}
.splicing-titles-tray{
    flex: 1.9;
    display: flex;
    justify-content: flex-start;
}
.splicing-titles-origin{
    flex: 2.6;
    display: flex;
    justify-content: flex-start;
}
.splicing-titles-destination{
    flex: 2;
    display: flex;
    justify-content: flex-start;
}
.title-tray-item, .splicing-table-title{
    margin-left: 40px;
}
.title-tray-origin{
    margin-left: 12px;
}
.title-tray-item, .title-tray-origin {
    margin-bottom: 5px !important;
}

.cube{
    width: 35px;
    height: 25px;
    border: 1px solid rgb(66, 66, 66);
    border-radius: 10px;
}
.circle{
    width: 15px;
    height: 15px;
    border: 1px solid rgb(66, 66, 66);
    border-radius: 50%;
}
.circle-right{
    margin-right: 16px;
}
.circle-left{
    margin-left: 10px;
}
.cube-right{
    margin-right: 5px;
}
.cube-left{
    margin-left: 5px;
}
.triangle{
    width: 0; 
    height: 0; 
    border-top: 20px solid transparent;
    border-bottom: 20px solid transparent; 
    border-right:15px solid lightgray; 
}
.triangle-right{
    margin-left: 10px;
    margin-right: 35px;
}
.triangle-left{
    margin-left: 35px;
    margin-right: 10px;
}
.type-row{
    border-left: 2px dotted rgb(74, 159, 239) !important;
}
.splicing-data-table{
    width: 100%;
}
.splicing-data-table >.v-data-table__wrapper>table>thead>tr>th{
    background-color:#e1f0f4 !important;
    color:rgb(109, 107, 107) !important;
}

.splicing-data-table >.v-data-table__wrapper>table>tbody>tr>td,
.splicing-data-table >.v-data-table__wrapper>table>tfoot>tr>td,
.splicing-data-table >.v-data-table__wrapper>table>thead>tr>td,
.splicing-data-table >.v-data-table__wrapper>table>thead>tr>th{
  padding: 0px 4px 0px 8px !important;
}

.splicing-data-table .v-data-table-header th:first-child,
.splicing-data-table >.v-data-table__wrapper>table>tbody>tr>td:first-child{
  padding-left: 39px !important;
}

</style>


// WEBPACK FOOTER //
// src/components/Home/Forms/_SpecialTabs/Splicing/SplicingTable.vue